import type { Vec3 } from "./vec3.js";

/** Axis-aligned bounding box. `min` is component-wise <= `max`. */
export interface AABB {
  min: Vec3;
  max: Vec3;
}

export function aabb(
  minX: number,
  minY: number,
  minZ: number,
  maxX: number,
  maxY: number,
  maxZ: number,
): AABB {
  return {
    min: { x: minX, y: minY, z: minZ },
    max: { x: maxX, y: maxY, z: maxZ },
  };
}

export function aabbFromCenterSize(center: Vec3, size: Vec3): AABB {
  const hx = size.x * 0.5;
  const hy = size.y * 0.5;
  const hz = size.z * 0.5;
  return {
    min: { x: center.x - hx, y: center.y - hy, z: center.z - hz },
    max: { x: center.x + hx, y: center.y + hy, z: center.z + hz },
  };
}

export function cloneAABB(b: AABB): AABB {
  return {
    min: { x: b.min.x, y: b.min.y, z: b.min.z },
    max: { x: b.max.x, y: b.max.y, z: b.max.z },
  };
}

export function aabbCenter(b: AABB): Vec3 {
  return {
    x: (b.min.x + b.max.x) * 0.5,
    y: (b.min.y + b.max.y) * 0.5,
    z: (b.min.z + b.max.z) * 0.5,
  };
}

export function aabbSize(b: AABB): Vec3 {
  return {
    x: b.max.x - b.min.x,
    y: b.max.y - b.min.y,
    z: b.max.z - b.min.z,
  };
}

/** Grows the box by `m` on every axis (negative shrinks). */
export function expandAABB(b: AABB, m: number): AABB {
  return {
    min: { x: b.min.x - m, y: b.min.y - m, z: b.min.z - m },
    max: { x: b.max.x + m, y: b.max.y + m, z: b.max.z + m },
  };
}

/** Grows the box per-axis. */
export function expandAABBv(b: AABB, m: Vec3): AABB {
  return {
    min: { x: b.min.x - m.x, y: b.min.y - m.y, z: b.min.z - m.z },
    max: { x: b.max.x + m.x, y: b.max.y + m.y, z: b.max.z + m.z },
  };
}

export function unionAABB(a: AABB, b: AABB): AABB {
  return {
    min: {
      x: Math.min(a.min.x, b.min.x),
      y: Math.min(a.min.y, b.min.y),
      z: Math.min(a.min.z, b.min.z),
    },
    max: {
      x: Math.max(a.max.x, b.max.x),
      y: Math.max(a.max.y, b.max.y),
      z: Math.max(a.max.z, b.max.z),
    },
  };
}

export function containsPoint(b: AABB, p: Vec3): boolean {
  return (
    p.x >= b.min.x &&
    p.x <= b.max.x &&
    p.y >= b.min.y &&
    p.y <= b.max.y &&
    p.z >= b.min.z &&
    p.z <= b.max.z
  );
}

/** Inclusive overlap test (touching faces count as overlapping). */
export function aabbOverlap(a: AABB, b: AABB): boolean {
  return (
    a.min.x <= b.max.x &&
    a.max.x >= b.min.x &&
    a.min.y <= b.max.y &&
    a.max.y >= b.min.y &&
    a.min.z <= b.max.z &&
    a.max.z >= b.min.z
  );
}

/** Strict overlap test — touching faces do NOT count. Used by collision. */
export function aabbOverlapStrict(a: AABB, b: AABB): boolean {
  return (
    a.min.x < b.max.x &&
    a.max.x > b.min.x &&
    a.min.y < b.max.y &&
    a.max.y > b.min.y &&
    a.min.z < b.max.z &&
    a.max.z > b.min.z
  );
}

/** Nearest point on/inside `b` to `p`. */
export function closestPointAABB(b: AABB, p: Vec3): Vec3 {
  return {
    x: p.x < b.min.x ? b.min.x : p.x > b.max.x ? b.max.x : p.x,
    y: p.y < b.min.y ? b.min.y : p.y > b.max.y ? b.max.y : p.y,
    z: p.z < b.min.z ? b.min.z : p.z > b.max.z ? b.max.z : p.z,
  };
}

export function closestPointAABBInto(out: Vec3, b: AABB, p: Vec3): Vec3 {
  out.x = p.x < b.min.x ? b.min.x : p.x > b.max.x ? b.max.x : p.x;
  out.y = p.y < b.min.y ? b.min.y : p.y > b.max.y ? b.max.y : p.y;
  out.z = p.z < b.min.z ? b.min.z : p.z > b.max.z ? b.max.z : p.z;
  return out;
}

/** Squared distance from `p` to the nearest point of `b` (0 when inside). */
export function distanceSqToAABB(b: AABB, p: Vec3): number {
  let d = 0;
  if (p.x < b.min.x) {
    const t = b.min.x - p.x;
    d += t * t;
  } else if (p.x > b.max.x) {
    const t = p.x - b.max.x;
    d += t * t;
  }
  if (p.y < b.min.y) {
    const t = b.min.y - p.y;
    d += t * t;
  } else if (p.y > b.max.y) {
    const t = p.y - b.max.y;
    d += t * t;
  }
  if (p.z < b.min.z) {
    const t = b.min.z - p.z;
    d += t * t;
  } else if (p.z > b.max.z) {
    const t = p.z - b.max.z;
    d += t * t;
  }
  return d;
}

const RAY_EPS = 1e-9;

/**
 * Slab-method ray/AABB intersection.
 *
 * @param origin  ray origin
 * @param dir     ray direction — SHOULD be normalized, the returned distance is
 *                measured in units of `dir`'s length
 * @param maxDist inclusive upper bound on the returned distance
 * @param box     the box to test
 * @param outNormal optional; receives the outward face normal of the entry face
 *                  (zero vector when the origin already lies inside the box)
 * @returns distance along the ray to the entry point, `0` if the origin is
 *          inside the box, or `null` when there is no hit within `maxDist`.
 */
export function raycastAABB(
  origin: Vec3,
  dir: Vec3,
  maxDist: number,
  box: AABB,
  outNormal?: Vec3,
): number | null {
  let tmin = 0;
  let tmax = maxDist;
  // 0/1/2 = x/y/z, -1 = origin started inside the slab set (no entry face).
  let hitAxis = -1;
  let hitSign = 0;

  // --- X slab ---
  if (dir.x > -RAY_EPS && dir.x < RAY_EPS) {
    if (origin.x < box.min.x || origin.x > box.max.x) return null;
  } else {
    const inv = 1 / dir.x;
    let t1 = (box.min.x - origin.x) * inv;
    let t2 = (box.max.x - origin.x) * inv;
    let sign = -1;
    if (t1 > t2) {
      const tmp = t1;
      t1 = t2;
      t2 = tmp;
      sign = 1;
    }
    if (t1 > tmin) {
      tmin = t1;
      hitAxis = 0;
      hitSign = sign;
    }
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return null;
  }

  // --- Y slab ---
  if (dir.y > -RAY_EPS && dir.y < RAY_EPS) {
    if (origin.y < box.min.y || origin.y > box.max.y) return null;
  } else {
    const inv = 1 / dir.y;
    let t1 = (box.min.y - origin.y) * inv;
    let t2 = (box.max.y - origin.y) * inv;
    let sign = -1;
    if (t1 > t2) {
      const tmp = t1;
      t1 = t2;
      t2 = tmp;
      sign = 1;
    }
    if (t1 > tmin) {
      tmin = t1;
      hitAxis = 1;
      hitSign = sign;
    }
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return null;
  }

  // --- Z slab ---
  if (dir.z > -RAY_EPS && dir.z < RAY_EPS) {
    if (origin.z < box.min.z || origin.z > box.max.z) return null;
  } else {
    const inv = 1 / dir.z;
    let t1 = (box.min.z - origin.z) * inv;
    let t2 = (box.max.z - origin.z) * inv;
    let sign = -1;
    if (t1 > t2) {
      const tmp = t1;
      t1 = t2;
      t2 = tmp;
      sign = 1;
    }
    if (t1 > tmin) {
      tmin = t1;
      hitAxis = 2;
      hitSign = sign;
    }
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return null;
  }

  if (outNormal) {
    outNormal.x = 0;
    outNormal.y = 0;
    outNormal.z = 0;
    if (hitAxis === 0) outNormal.x = hitSign;
    else if (hitAxis === 1) outNormal.y = hitSign;
    else if (hitAxis === 2) outNormal.z = hitSign;
  }

  return tmin;
}
